/**
 * Notification sound utility using Web Audio API
 * Plays a short beep when new notifications arrive
 */

let audioContext: AudioContext | null = null;
let isUnlocked = false;

// Unlock AudioContext on first user interaction (browser autoplay policy)
function ensureUnlocked(): void {
  if (isUnlocked) return;

  const unlock = () => {
    if (audioContext && audioContext.state === 'suspended') {
      void audioContext.resume();
    }
    isUnlocked = true;
    document.removeEventListener('click', unlock);
    document.removeEventListener('keydown', unlock);
  };

  document.addEventListener('click', unlock, { once: true });
  document.addEventListener('keydown', unlock, { once: true });
}

function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new AudioContext();
    ensureUnlocked();
  }
  return audioContext;
}

/**
 * Play a short notification beep sound
 * Uses Web Audio API OscillatorNode (no external audio files needed)
 */
export function playNotificationSound(): void {
  if (localStorage.getItem('lov_notification_sound_enabled') === 'false') return;

  try {
    const ctx = getAudioContext();

    if (ctx.state === 'suspended') {
      // Can't play yet — browser hasn't unlocked audio
      return;
    }

    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(800, ctx.currentTime);

    // Quick fade in/out for clean beep
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.02);
    gainNode.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.2);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.2);
  } catch {
    // Silently fail — notification toast still shows
  }
}
